import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, MapPin, Crosshair, Globe2, FileWarning,
  ChevronDown, ChevronRight as ChevronRightIcon, ExternalLink, Info,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import './AdminLayout.css';
import './AdminLocationQuality.css';

// iter322 Phase C — Admin Location-Quality Dashboard
// =====================================================
// Self-contained admin page that surfaces location-completeness
// metrics for the public marketplace feed. ALL numbers come from
// the same canonical filter as /api/ads (iter322B/iter329/iter332/
// iter333), so the headline "Total real marketplace ads" never
// disagrees with what users see on Shop/Home/Ladies Hub.

const PRIMARY_LABELS_EN = {
  total: 'Total real marketplace ads',
  gps_confirmed: 'GPS-confirmed location',
  manual_location: 'Manual location',
  country_only: 'Country only',
  missing_country: 'Missing country',
};
const PRIMARY_LABELS_AR = {
  total: 'إجمالي إعلانات السوق الحقيقية',
  gps_confirmed: 'موقع مؤكد بالـ GPS',
  manual_location: 'موقع يدوي',
  country_only: 'الدولة فقط',
  missing_country: 'بدون دولة',
};

const DIAGNOSTIC_LABELS_EN = {
  missing_city_or_region: 'Missing city / state / town',
  needs_repair: 'Needs location repair',
  complete_pct: 'Complete location (%)',
};
const DIAGNOSTIC_LABELS_AR = {
  missing_city_or_region: 'بدون مدينة / ولاية / بلدة',
  needs_repair: 'بحاجة لإصلاح الموقع',
  complete_pct: 'موقع كامل (%)',
};

const BUCKET_LABELS_EN = {
  ...PRIMARY_LABELS_EN,
  missing_city_or_region: DIAGNOSTIC_LABELS_EN.missing_city_or_region,
  needs_repair: DIAGNOSTIC_LABELS_EN.needs_repair,
  complete: 'Complete location',
};
const BUCKET_LABELS_AR = {
  ...PRIMARY_LABELS_AR,
  missing_city_or_region: DIAGNOSTIC_LABELS_AR.missing_city_or_region,
  needs_repair: DIAGNOSTIC_LABELS_AR.needs_repair,
  complete: 'موقع كامل',
};

const PRIMARY_KPI_ORDER = ['total', 'gps_confirmed', 'manual_location', 'country_only', 'missing_country'];
const PRIMARY_KPI_ICONS = {
  total: MapPin,
  gps_confirmed: Crosshair,
  manual_location: MapPin,
  country_only: Globe2,
  missing_country: FileWarning,
};

export default function AdminLocationQuality() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const isArabic = lang === 'ar';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeBucket, setActiveBucket] = useState(null);
  const [bucketData, setBucketData] = useState(null);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [bucketPage, setBucketPage] = useState(1);
  const [expandedCountries, setExpandedCountries] = useState(() => new Set());

  const apiBase = getApiBase();

  const fetchDashboard = useCallback(async () => {
    if (!token) {
      setError(isArabic ? 'مطلوب تسجيل الدخول' : 'Authentication required');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/api/admin/location-quality`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(String(e?.message || e));
      toast.error(isArabic ? 'تعذر تحميل البيانات' : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [token, apiBase, isArabic]);

  const fetchBucket = useCallback(async (bucket, page = 1) => {
    if (!token || !bucket) return;
    setBucketLoading(true);
    try {
      const res = await fetch(
        `${apiBase}/api/admin/location-quality/ads?bucket=${encodeURIComponent(bucket)}&page=${page}&page_size=25`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setBucketData(json);
    } catch (e) {
      toast.error(isArabic ? 'تعذر تحميل قائمة الإعلانات' : 'Failed to load ad list');
    } finally {
      setBucketLoading(false);
    }
  }, [token, apiBase, isArabic]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const onCardClick = useCallback((bucket) => {
    setActiveBucket(bucket);
    setBucketPage(1);
    fetchBucket(bucket, 1);
    // Scroll the list panel into view after a short tick so the
    // mobile operator sees it open immediately under the cards.
    setTimeout(() => {
      const el = document.getElementById('iter322c-bucket-panel');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }, [fetchBucket]);

  const onBucketPageChange = useCallback((newPage) => {
    if (!activeBucket || newPage < 1) return;
    setBucketPage(newPage);
    fetchBucket(activeBucket, newPage);
  }, [activeBucket, fetchBucket]);

  const closeBucketPanel = useCallback(() => {
    setActiveBucket(null);
    setBucketData(null);
    setBucketPage(1);
  }, []);

  const toggleCountry = useCallback((countryKey) => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      if (next.has(countryKey)) next.delete(countryKey);
      else next.add(countryKey);
      return next;
    });
  }, []);

  // Group breakdown rows by country → region → city.
  const countryTree = useMemo(() => {
    if (!data?.breakdown_by_country) return [];
    const map = new Map();
    for (const row of data.breakdown_by_country) {
      const cKey = row.country || '__none__';
      if (!map.has(cKey)) {
        map.set(cKey, {
          country: row.country,
          country_name: row.country_name,
          total: 0,
          regions: new Map(),
        });
      }
      const cEntry = map.get(cKey);
      cEntry.total += row.count;
      const rKey = row.region || '__none__';
      if (!cEntry.regions.has(rKey)) {
        cEntry.regions.set(rKey, { region: row.region, total: 0, cities: [] });
      }
      const rEntry = cEntry.regions.get(rKey);
      rEntry.total += row.count;
      rEntry.cities.push({ city: row.city, count: row.count });
    }
    const arr = Array.from(map.values()).map((c) => ({
      ...c,
      regions: Array.from(c.regions.values()).sort((a, b) => b.total - a.total),
    }));
    arr.sort((a, b) => b.total - a.total);
    return arr;
  }, [data]);

  if (loading) {
    return (
      <div className="admin-page" data-testid="admin-location-quality-loading">
        <div className="admin-page-loading">
          <Loader2 className="admin-spinner" size={28} />
          <span>{isArabic ? 'جاري التحميل…' : 'Loading…'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page" data-testid="admin-location-quality-error">
        <div className="admin-card error-card">
          <strong>{isArabic ? 'خطأ' : 'Error'}:</strong> {error}
        </div>
        <button
          type="button"
          className="admin-btn"
          onClick={fetchDashboard}
          data-testid="admin-location-quality-retry"
        >
          {isArabic ? 'إعادة المحاولة' : 'Retry'}
        </button>
      </div>
    );
  }

  const primary = data?.primary || {};
  const diag = data?.diagnostics || {};

  return (
    <div
      className="admin-page admin-location-quality"
      dir={isArabic ? 'rtl' : 'ltr'}
      data-testid="admin-location-quality"
    >
      <header className="loc-q-header">
        <div>
          <h1 className="loc-q-title" data-testid="loc-q-title">
            {isArabic ? 'جودة بيانات الموقع' : 'Location-Quality Dashboard'}
          </h1>
          <p className="loc-q-sub">
            {isArabic
              ? 'مقاييس اكتمال الموقع للسوق الحقيقي'
              : 'Location-completeness metrics for the real marketplace'}
          </p>
        </div>
        <button
          type="button"
          className="admin-btn admin-btn-ghost"
          onClick={fetchDashboard}
          data-testid="loc-q-refresh"
          aria-label={isArabic ? 'تحديث' : 'Refresh'}
        >
          <RefreshCw size={16} />
          <span>{isArabic ? 'تحديث' : 'Refresh'}</span>
        </button>
      </header>

      <div className="loc-q-note" data-testid="loc-q-exclusions-note" role="note">
        <Info size={14} />
        <span>
          {isArabic
            ? 'الأرقام الرئيسية تستثني سجلات الاختبار والبذور والمحذوفة والمرفوضة والمخفية والنظامية.'
            : 'Primary numbers exclude test, seed, deleted, rejected, hidden, and system records.'}
        </span>
      </div>

      <section className="loc-q-section" aria-labelledby="loc-q-primary-h">
        <h2 id="loc-q-primary-h" className="loc-q-section-title">
          {isArabic ? 'الأرقام الرئيسية' : 'Primary numbers'}
        </h2>
        <div className="loc-q-kpi-grid" data-testid="loc-q-primary-grid">
          {PRIMARY_KPI_ORDER.map((key) => {
            const Icon = PRIMARY_KPI_ICONS[key];
            const label = isArabic ? PRIMARY_LABELS_AR[key] : PRIMARY_LABELS_EN[key];
            const value = primary[key] ?? 0;
            const isActive = activeBucket === key;
            return (
              <button
                key={key}
                type="button"
                className={`loc-q-kpi-card${isActive ? ' active' : ''}`}
                onClick={() => onCardClick(key)}
                data-testid={`loc-q-kpi-${key}`}
                aria-pressed={isActive}
              >
                <span className="loc-q-kpi-icon"><Icon size={18} /></span>
                <span className="loc-q-kpi-value" data-testid={`loc-q-kpi-${key}-value`}>
                  {value.toLocaleString(isArabic ? 'ar' : 'en')}
                </span>
                <span className="loc-q-kpi-label">{label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="loc-q-section" aria-labelledby="loc-q-diag-h">
        <h2 id="loc-q-diag-h" className="loc-q-section-title diag">
          {isArabic ? 'تشخيصات' : 'Diagnostics'}
        </h2>
        <div className="loc-q-diag-grid" data-testid="loc-q-diagnostics-grid">
          <button
            type="button"
            className={`loc-q-diag-card${activeBucket === 'missing_city_or_region' ? ' active' : ''}`}
            onClick={() => onCardClick('missing_city_or_region')}
            data-testid="loc-q-diag-missing-city"
          >
            <span className="loc-q-diag-label">
              {isArabic ? DIAGNOSTIC_LABELS_AR.missing_city_or_region : DIAGNOSTIC_LABELS_EN.missing_city_or_region}
            </span>
            <span className="loc-q-diag-value">
              {(diag.missing_city_or_region ?? 0).toLocaleString(isArabic ? 'ar' : 'en')}
            </span>
          </button>
          <button
            type="button"
            className={`loc-q-diag-card${activeBucket === 'needs_repair' ? ' active' : ''}`}
            onClick={() => onCardClick('needs_repair')}
            data-testid="loc-q-diag-needs-repair"
          >
            <span className="loc-q-diag-label">
              {isArabic ? DIAGNOSTIC_LABELS_AR.needs_repair : DIAGNOSTIC_LABELS_EN.needs_repair}
            </span>
            <span className="loc-q-diag-value">
              {(diag.needs_repair ?? 0).toLocaleString(isArabic ? 'ar' : 'en')}
            </span>
          </button>
          <div className="loc-q-diag-card readonly" data-testid="loc-q-diag-complete-pct">
            <span className="loc-q-diag-label">
              {isArabic ? DIAGNOSTIC_LABELS_AR.complete_pct : DIAGNOSTIC_LABELS_EN.complete_pct}
            </span>
            <span className="loc-q-diag-value">
              {(diag.complete_pct ?? 0).toLocaleString(isArabic ? 'ar' : 'en')}%
            </span>
            <span className="loc-q-diag-sub">
              {(diag.complete_count ?? 0).toLocaleString(isArabic ? 'ar' : 'en')} / {(primary.total ?? 0).toLocaleString(isArabic ? 'ar' : 'en')}
            </span>
          </div>
        </div>
      </section>

      <section className="loc-q-section" aria-labelledby="loc-q-breakdown-h">
        <h2 id="loc-q-breakdown-h" className="loc-q-section-title">
          {isArabic ? 'التوزيع الجغرافي' : 'Breakdown by country → state → city'}
        </h2>
        <div className="loc-q-breakdown" data-testid="loc-q-breakdown">
          {countryTree.length === 0 ? (
            <div className="loc-q-empty">
              {isArabic ? 'لا توجد بيانات للعرض.' : 'No data to show yet.'}
            </div>
          ) : (
            countryTree.map((c) => {
              const cKey = c.country || '__none__';
              const expanded = expandedCountries.has(cKey);
              return (
                <div key={cKey} className="loc-q-country-row" data-testid={`loc-q-country-${cKey}`}>
                  <button
                    type="button"
                    className="loc-q-country-head"
                    onClick={() => toggleCountry(cKey)}
                    aria-expanded={expanded}
                    data-testid={`loc-q-country-toggle-${cKey}`}
                  >
                    {expanded ? <ChevronDown size={16} /> : <ChevronRightIcon size={16} />}
                    <span className="loc-q-country-name">
                      {c.country_name || c.country || (isArabic ? 'بدون دولة' : 'No country')}
                    </span>
                    <span className="loc-q-country-count">
                      {c.total.toLocaleString(isArabic ? 'ar' : 'en')}
                    </span>
                  </button>
                  {expanded && (
                    <div className="loc-q-regions">
                      {c.regions.map((r) => {
                        const rKey = `${cKey}::${r.region || '__none__'}`;
                        return (
                          <div key={rKey} className="loc-q-region-row">
                            <div className="loc-q-region-head">
                              <span className="loc-q-region-name">
                                {r.region || (isArabic ? 'بدون ولاية' : 'No region')}
                              </span>
                              <span className="loc-q-region-count">
                                {r.total.toLocaleString(isArabic ? 'ar' : 'en')}
                              </span>
                            </div>
                            <div className="loc-q-cities">
                              {r.cities.map((city, idx) => (
                                <div
                                  key={`${rKey}-${city.city || 'none'}-${idx}`}
                                  className="loc-q-city-row"
                                  data-testid="loc-q-city-row"
                                >
                                  <span>{city.city || (isArabic ? 'بدون مدينة' : 'No city')}</span>
                                  <span>{city.count.toLocaleString(isArabic ? 'ar' : 'en')}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>

      {activeBucket && (
        <section
          id="iter322c-bucket-panel"
          className="loc-q-section loc-q-bucket-panel"
          data-testid="loc-q-bucket-panel"
          aria-labelledby="loc-q-bucket-h"
        >
          <header className="loc-q-bucket-head">
            <h2 id="loc-q-bucket-h" className="loc-q-section-title">
              {isArabic ? BUCKET_LABELS_AR[activeBucket] : BUCKET_LABELS_EN[activeBucket]}
              {' '}
              <span className="loc-q-bucket-total" data-testid="loc-q-bucket-total">
                ({(bucketData?.total ?? 0).toLocaleString(isArabic ? 'ar' : 'en')})
              </span>
            </h2>
            <button
              type="button"
              className="admin-btn admin-btn-ghost"
              onClick={closeBucketPanel}
              data-testid="loc-q-bucket-close"
            >
              {isArabic ? 'إغلاق' : 'Close'}
            </button>
          </header>
          {bucketLoading ? (
            <div className="admin-page-loading">
              <Loader2 className="admin-spinner" size={20} />
            </div>
          ) : (
            <>
              {(bucketData?.items || []).length === 0 ? (
                <div className="loc-q-empty" data-testid="loc-q-bucket-empty">
                  {isArabic ? 'لا توجد إعلانات في هذه الفئة.' : 'No ads in this bucket.'}
                </div>
              ) : (
                <ul className="loc-q-ad-list" data-testid="loc-q-ad-list">
                  {(bucketData?.items || []).map((ad) => (
                    <li key={ad.ad_id} className="loc-q-ad-row" data-testid={`loc-q-ad-row-${ad.ad_id}`}>
                      <div className="loc-q-ad-main">
                        <div className="loc-q-ad-title">{ad.title || '—'}</div>
                        <div className="loc-q-ad-meta">
                          <span>{ad.category || '—'}</span>
                          <span>{ad.country_name || ad.country || (isArabic ? 'بدون دولة' : 'No country')}</span>
                          {ad.region && <span>{ad.region}</span>}
                          {ad.city && <span>{ad.city}</span>}
                          {ad.location_lat != null && ad.location_lng != null && (
                            <span className="loc-q-pill gps">GPS</span>
                          )}
                          {!ad.location_confirmed && (
                            <span className="loc-q-pill warn">
                              {isArabic ? 'غير مؤكد' : 'unconfirmed'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="loc-q-ad-actions">
                        <button
                          type="button"
                          className="admin-btn admin-btn-link"
                          onClick={() => navigate(`/admin/ads?ad_id=${encodeURIComponent(ad.ad_id)}`)}
                          data-testid={`loc-q-ad-open-${ad.ad_id}`}
                          title={isArabic ? 'فتح في إدارة الإعلانات' : 'Open in Admin Ads'}
                        >
                          <ExternalLink size={14} />
                          <span>{isArabic ? 'فتح' : 'Open'}</span>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {(bucketData?.total ?? 0) > (bucketData?.page_size ?? 25) && (
                <div className="loc-q-pager">
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    disabled={bucketPage <= 1}
                    onClick={() => onBucketPageChange(bucketPage - 1)}
                    data-testid="loc-q-bucket-prev"
                  >
                    {isArabic ? 'السابق' : 'Prev'}
                  </button>
                  <span className="loc-q-pager-info">
                    {isArabic ? `صفحة ${bucketPage}` : `Page ${bucketPage}`}
                  </span>
                  <button
                    type="button"
                    className="admin-btn admin-btn-ghost"
                    disabled={(bucketPage * (bucketData?.page_size ?? 25)) >= (bucketData?.total ?? 0)}
                    onClick={() => onBucketPageChange(bucketPage + 1)}
                    data-testid="loc-q-bucket-next"
                  >
                    {isArabic ? 'التالي' : 'Next'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
